import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';
import { isDefault, isThankYouLotto } from '../../../../utils/template';
import { PRIMARY_NUMBERS, BONUS_NUMBERS, isValidLine } from '../../helpers/linesHelper';
import useLinesHelper from '../../helpers/useLinesHelper';

import NumberPanel from './NumberPanel';
import NumberPanelTitle from './NumberPanelTitle';

import DivButton, { createInteractiveProps } from 'components/common/DivButton';
import { LineProps, SelectSignalLottery } from 'components/play/Types/Types';
import { RootState } from 'components/play/redux/store';
import { numberArray } from 'utils/helpers';

export default function MobileLine({ lineNumbers, lineIndex, stake }: LineProps) {
    const { t } = useTranslation();
    const { numbersPerLine, setRandomLine, removeLine } = useLinesHelper();
    const [panelVisible, setPanelVisible] = useState(false);
    const { lottery } = useSelector((state: RootState) => state.playStore);
    const [selectSignal, setSelectSignal] = useState<SelectSignalLottery>({ isInitialized: false, ballIndex: 0 });
    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);

    const getBall = (number: number, index: number, numbersIndex: number, isFirstBonusBall?: boolean) => {
        const choosedBall = number >= 0 ? `ball-${numbersIndex}` : '';
        const firstBonusBall = isFirstBonusBall ? 'first-bonus-ball' : '';

        return (
            <DivButton
                key={`${numbersIndex}-${index}`}
                className={`ball ${choosedBall} ${firstBonusBall}`}
                onClick={
                    isDefault()
                        ? () => setSelectSignal({ ballIndex: number, isInitialized: true })
                        : () => setPanelVisible(true)
                }
            >
                {number}
            </DivButton>
        );
    };

    const checked = isValidLine(lottery, lineNumbers, numbersPerLine);
    const lineClass = 'row purchase-line-container-mobile purchase-line-container-mobile-lottery add-line line-chosen';

    const renderPickerCheckedView = () => (
        <div className="row purchase-line-container-mobile fade-in mobile-picker-checked">
            <div className="row-balls-container checked d-flex flex-row justify-content-between align-items-center g-0 w-auto position-relative">
                <div
                    className={`mobile-new-line d-flex flex-wrap bonus-balls-${numbersPerLine[BONUS_NUMBERS]}`}>
                    {numberArray(numbersPerLine[PRIMARY_NUMBERS]).map((i) => {
                        return getBall(lineNumbers[PRIMARY_NUMBERS]?.[i], i, PRIMARY_NUMBERS);
                    })}
                    {numberArray(numbersPerLine[BONUS_NUMBERS]).map((i) => {
                        const isFirstBonus = i === 0;
                        return getBall(lineNumbers[BONUS_NUMBERS]?.[i], i, BONUS_NUMBERS, isFirstBonus);
                    })}
                </div>
            </div>
            <div className="row-balls-container-right w-auto g-0 position-relative">
                <i
                    className="fas fa-trash mobile-delete-line "
                    {...createInteractiveProps(() => removeLine(lineIndex))}
                    aria-label={t('playPicker.clear')}
                />
            </div>
        </div>
    );

    const renderPickerUncheckedView = () => (
        <div className="row purchase-line-container-mobile fade-in">
            <div className="title-container position-relative g-0">
                <h1 className="title text-center">
                    <NumberPanelTitle />
                </h1>
            </div>
            <div className="row-balls-container d-flex flex-column">
                <div className={`mobile-new-line unchecked d-flex justify-content-center bonus-balls-${numbersPerLine[BONUS_NUMBERS]}`}>
                    {numberArray(numbersPerLine[PRIMARY_NUMBERS]).map((i) => {
                        return getBall(lineNumbers[PRIMARY_NUMBERS]?.[i], i, PRIMARY_NUMBERS);
                    })}
                    {numberArray(numbersPerLine[BONUS_NUMBERS]).map((i) => {
                        const isFirstBonus = i === 0;
                        return getBall(lineNumbers[BONUS_NUMBERS]?.[i], i, BONUS_NUMBERS, isFirstBonus);
                    })}
                </div>
                <button
                    type="button"
                    onClick={() => setRandomLine(lineIndex)}
                    className="btn btn-primary button quick-pick-mobile mx-auto"
                >
                    {t('playPicker.quickPick')}
                </button>
            </div>
        </div>
    );

    function getThankYouLottoView() {
        return (
            <>
                <div className="col-12">
                    <NumberPanel
                        selectSignal={selectSignal}
                        lineNumbers={lineNumbers}
                        lineIndex={lineIndex}
                        stake={stake}
                    />
                </div>
            </>
        );
    }

    const baseView = () => {
        if (isTemplateThankYouLotto) {
            return getThankYouLottoView();
        }

        return (
            <>
                <div className={`${lineClass} fade-in ${checked ? 'mobile-picker-checked' : ''}`}>
                    <div className="col-12">
                        <h5 className="title">
                            <NumberPanelTitle />
                        </h5>
                    </div>
                    <div className="col-12 row-balls-container">
                        <div className="row mobile-new-line">
                            {numberArray(numbersPerLine[PRIMARY_NUMBERS]).map((i) =>
                                getBall(lineNumbers[PRIMARY_NUMBERS]?.[i], i, PRIMARY_NUMBERS)
                            )}
                            {numberArray(numbersPerLine[BONUS_NUMBERS]).map((i) =>
                                getBall(lineNumbers[BONUS_NUMBERS]?.[i], i, BONUS_NUMBERS)
                            )}
                        </div>
                    </div>
                    <div className="col-12">
                        <button
                            type="button"
                            onClick={() => removeLine(lineIndex)}
                            className="btn btn-secondary button mobile-delete-line"
                        >
                            {t('playPicker.clear')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setRandomLine(lineIndex)}
                            className="btn btn-primary button quick-pick-mobile"
                        >
                            {t('playPicker.quickPick')}
                        </button>
                    </div>
                </div>
                {panelVisible && (
                    <div className="d-block purchase-line-container rounded">
                        <div className="mobile-top d-flex justify-content-between align-items-center">
                            <h5 className="title">
                                <NumberPanelTitle />
                            </h5>
                            <button
                                type="button"
                                onClick={() => setPanelVisible(false)}
                                className="close"
                                aria-label="Close"
                            >
                                <span aria-hidden="true">
                                    <i className="far fa-times-circle" />
                                </span>
                            </button>
                        </div>
                        <NumberPanel
                            selectSignal={selectSignal}
                            lineNumbers={lineNumbers}
                            lineIndex={lineIndex}
                            stake={stake}
                        />
                        <div className="btn-pick">
                            <button
                                type="button"
                                onClick={() => setPanelVisible(false)}
                                className={`btn btn-primary btn-submit add-ticket-mobile ${checked ? '' : 'disabled'}`}
                            >
                                {t('playPicker.lottery.addThisTicket')}
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    };

    const defaultView = () => (
        <>
            {lineNumbers[PRIMARY_NUMBERS].length > 0 ? renderPickerCheckedView() : renderPickerUncheckedView()}
            <NumberPanel selectSignal={selectSignal} lineNumbers={lineNumbers} lineIndex={lineIndex} stake={stake} />
        </>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}
